<template>
  <div class="app">
    <Loading v-if="loading" />
    <main class="content" v-else>
      
      <!-- Header -->
      <div class="page-header">
        <h3>Smoelenboek</h3>
        <input
        
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Zoek medewerker..."
        />
      </div>
      <hr>

      <div class="grid">
        <div
          v-for="e in filteredEmployees"
          :key="e.Medewerker"
          class="card"
        >
          <div class="card-header">
            <template v-if="getPhotoSrc(e)">
              <img :src="getPhotoSrc(e)" alt="Profielfoto" class="avatar" />
            </template>
            <template v-else>
              <div class="avatar avatar--initials" aria-hidden="true">{{ getInitials(e) }}</div>
            </template>

            <div class="card-title">
              <h3 class="name">{{ getFullName(e) }}</h3>
              <p class="role">{{ e.Omschrijving }}</p>
              <a class="link">{{ e['E-mail_werk'] }}</a>
              <a v-if="getPhone(e)" class="phone">{{ getPhone(e) }}</a>
              <a v-if="e.Intern_nummer" class="phone">{{ e.Intern_nummer }}</a>
            </div>
          </div>

          <div class="roster">
            <h4 class="roster-title">Werkdagen</h4>
            <div class="chips-grid">
              <div
                v-for="day in getSchedule(e)"
                :key="day.key"
                class="chip"
                :class="day.status !== 'Vrij' ? 'chip--work' : 'chip--free'"
                :title="day.status !== 'Vrij' ? (day.label + ': werkt') : (day.label + ': vrij')"
              >
                <span v-if="day.status !== 'Vrij'" class="chip-dot chip-dot--work"></span>
                <span class="chip-label">{{ day.short }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import request from "@/functions/request.js";

export default {
  name: "EmployeePage",
  components: { Loading },
  data() {
    return {
      employees: null,
      loading: false,
      searchQuery: "",
    };
  },
  created() {
    this.getData();
  },
  computed: {
    filteredEmployees() {
      if (!this.searchQuery) return this.employees || [];
      const q = this.searchQuery.toLowerCase();
      return (this.employees || []).filter(e =>
        this.getFullName(e).toLowerCase().includes(q) ||
        (e["E-mail_werk"] || "").toLowerCase().includes(q) ||
        (this.getPhone(e) || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    async getData() {
      this.loading = true;
      this.error = null;
      try {
        const response = await request(`employees/`, "GET");
        console.log(response)
        this.employees = Array.isArray(response.employees) ? response.employees : [];
      } catch (e) {
        console.error("Error fetching employees:", e);
        this.error = "Kon gegevens niet laden.";
        this.employees = [];
      } finally {
        this.loading = false;
      }
    },
    getFullName(e) {
      const parts = [e.Roepnaam];
      if (e.Voorvoegsel) parts.push(e.Voorvoegsel);
      parts.push(e.Achternaam);
      return parts.join(" ");
    },
    getPhotoSrc(e) {
      if (!e.Afbeelding) return null;
      return `data:image/jpeg;base64,${e.Afbeelding}`;
    },
    getInitials(e) {
      const n = this.getFullName(e).trim();
      const parts = n.split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const last = parts[parts.length - 1] ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },
    getPhone(e) {
      const mobile = (e.Mobiel_werk || "").toString().trim();
      const work = (e.Telefoonnr_werk || "").toString().trim();
      return mobile || work || "";
    },
    getSchedule(e) {
      const daysBase = [
        { key: "Maandag", label: "Maandag", working: !!e.Maandag_werkdag, hours: Number(e.Uren_Maandag || 0) },
        { key: "Dinsdag", label: "Dinsdag", working: !!e.Dinsdag_werkdag, hours: Number(e.Uren_Dinsdag || 0) },
        { key: "Woensdag", label: "Woensdag", working: !!e.Woensdag_werkdag, hours: Number(e.Uren_Woensdag || 0) },
        { key: "Donderdag", label: "Donderdag", working: !!e.Donderdag_werkdag, hours: Number(e.Uren_Donderdag || 0) },
        { key: "Vrijdag", label: "Vrijdag", working: !!e.Vrijdag_werkdag, hours: Number(e.Uren_Vrijdag || 0) },
        { key: "Zaterdag", label: "Zaterdag", working: !!e.Zaterdag_werkdag, hours: Number(e.Uren_Zaterdag || 0) },
      ];
      const shortMap = { Maandag: "Ma", Dinsdag: "Di", Woensdag: "Wo", Donderdag: "Do", Vrijdag: "Vr", Zaterdag: "Za" };
      return daysBase.map(d => {
        let status;
        if (!d.working || d.hours <= 0) status = "Vrij";
        else if (d.hours < 8) status = "Dagdeel";
        else status = "Hele dag";
        return { ...d, short: shortMap[d.key], status };
      });
    },
  },
};
</script>

<style scoped>
.app {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc, #ffffff);
}

.content {
  padding: 40px 24px;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: left;
  gap: 20px;
  align-items: center;
  margin-bottom: 32px;
}

.search-input {
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  min-width: 250px;
  border: 2px solid #ef7622;
}
.search-input:focus {
  border-color: #0284c7;
  box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.2);
}

.grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
}


.card {
    border: 1px solid rgba(226, 232, 240, 0.9);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(2px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease;
}

.card:hover {
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
}

.card-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px;
    text-align: center;
    height: 100%;
}

.avatar {
    height: 96px;
    width: 96px;
    border-radius: 24px;
    object-fit: cover;
    border: 1px solid rgba(226, 232, 240, 1);
}

.avatar--initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #bae6fd, #38bdf8);
    color: #0c4a6e;
    font-weight: 600;
    font-size: 24px;
}

.card-title .name {
    font-weight: 600;
    font-size: 20px;
    color: #0f172a;
    margin: 0;
}

.card-title .role {
    color: #475569;
    font-size: 14px;
    margin: 2px 0 0;
}

.card-title .link {
    display: block;
    margin-top: 6px;
    color: #0284c7;
    font-size: 14px;
    text-decoration: none;
}

.card-title .link:hover {
    color: #0369a1;
}

.card-title .phone {
    display: block;
    margin-top: 4px;
    color: #334155;
    font-size: 14px;
    text-decoration: none;
}

.card-title .phone:hover {
    color: #0f172a;
}

.roster {
    border-top: 1px solid #e2e8f0;
    padding: 16px 24px;
}

.roster-title {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
}

.chips-grid {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    align-items: stretch;
}

.chip {
    box-sizing: border-box;
    height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-radius: 18px;
    padding: 8px 12px;
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1.1;
    border: 1px solid transparent;
    background-clip: padding-box;
    overflow: hidden;
}

.chip--work {
    background: rgba(22, 163, 74, 0.08);
    color: #166534;
    border-color: #86efac;
}

.chip--free {
    background: #f1f5f9;
    color: #475569;
    border-color: #e2e8f0;
}

.chip-dot {
    width: 10px;
    height: 10px;
    border-radius: 9999px;
    margin-bottom: 6px;
    display: inline-block;
    flex: 0 0 auto;
}

.chip-dot--work {
    background: #16a34a;
}

.chip-label {
    font-weight: 700;
    letter-spacing: 0.02em;
}

.chip--free::before {
    content: '';
    display: block;
    width: 10px;
    height: 10px;
    margin-bottom: 6px;
}
</style>
